import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { AnimatedViews } from '@garnish/core';

import { MenuDietaryPreferences } from '@sg/shared-fe-features-menu';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountDietaryRestrictionsLoading = () => {
  return (
    <MenuDietaryPreferences.Container withoutHorizontalSpacing>
      <AnimatedViews.Pulse>
        <View style={styles.descriptionLineFull} />
        <View style={styles.descriptionLineShort} />
      </AnimatedViews.Pulse>

      <MenuDietaryPreferences.ButtonsContainer>
        {CHIP_WIDTHS.map((width, index) => (
          <RestrictionChipPlaceholder key={index} index={index + 1} width={width} />
        ))}
      </MenuDietaryPreferences.ButtonsContainer>

      <AnimatedViews.Pulse index={CHIP_WIDTHS.length + 1} style={styles.submitButton} />

      <AnimatedViews.Pulse index={CHIP_WIDTHS.length + 2}>
        <View style={styles.noticeLineFull} />
        <View style={styles.noticeLineLong} />
        <View style={styles.noticeLineShort} />
        <View style={styles.noticeLineSeparated} />
      </AnimatedViews.Pulse>
    </MenuDietaryPreferences.Container>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const RestrictionChipPlaceholder = (props: RestrictionChipPlaceholderProps) => {
  const { index, width } = props;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const chipWidthStyle = useStyle(() => ({ width }), [width]);
  const chipStyles = [styles.chip, chipWidthStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return <AnimatedViews.Pulse index={index} style={chipStyles} />;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CHIP_WIDTHS = [218, 96, 90, 97, 89, 122, 121, 135, 134, 108];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  descriptionLineFull: {
    width: '100%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  descriptionLineShort: {
    width: '20%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  chip: {
    height: 48,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  submitButton: {
    alignSelf: 'center',
    width: 230,
    height: 64,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  noticeLineFull: {
    width: '100%',
    height: 10,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  noticeLineLong: {
    width: '96%',
    height: 10,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  noticeLineShort: {
    width: '14%',
    height: 10,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  noticeLineSeparated: {
    width: '92%',
    height: 10,
    marginTop: 22,
    marginBottom: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RestrictionChipPlaceholderProps = {
  index: number;
  width: number;
};
